import { PopUpHandler } from '../../../shared/PopupHandler';
import { FC, useState, useEffect, useRef, ReactNode } from 'react';
import { fetchUploaderIframeURL } from './service';
import { useUserContext } from '../../../../providers/UserProvider';
import { Button } from '../../../shared/Button';
import { LogoSpinner } from '../../../shared/LogoSpinner/LogoSpinner';
import { Prompt } from '../../../shared/Prompt';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import {
  useTagsObjectQuery,
  TagsObjectQuery,
} from '../../../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import './DocumentUploader.scss';
import { xTenantId } from '../../../../consts';
import { Paragraph } from '../../../shared/Paragraph';

type VerificationState =
  | { step: 'id'; status: 'loading' | 'idle' | 'failed'; view: 'closed' | 'prompt' | 'open' }
  | { step: 'address'; status: 'loading' | 'idle' | 'failed'; view: 'closed' | 'prompt' | 'open' }
  | { step: 'complete'; status: 'success'; view: 'closed' | 'prompt' | 'open' };

interface AddressVerificationConfig {
  merchantkey: string;
  name: string;
  surname: string;
  middlename?: string;
  street: string;
  city: string;
  postcode: string;
  country: string;
  countrycode: string;
  locale: string;
  reference: string;
  onInit: (data: never) => void;
  onComplete: (data: never) => void;
  onFail: (data: never) => void;
}

const UPLOAD_COMPLETED_KEY = 'doc_upload_completed';

export const DocumentUploader: FC = () => {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [verificationState, setVerificationState] = useState<VerificationState>({
    step: 'id',
    status: 'idle',
    view: 'closed',
  });
  const [addressVerificationInitialized, setAddressVerificationInitialized] = useState(false);
  const [isAddressVerificationLoading, setIsAddressVerificationLoading] = useState(true);

  const { userProfileId, compliance, user } = useUserContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [addressVerificationElement, setAddressVerificationElement] = useState<HTMLDivElement | null>(null);
  const { t } = useTranslations();
  const isDocUploaderEnabled = JSON.parse(process.env.REACT_APP_ENABLE_DOCUPLOAD as string);

  const { data } = useTagsObjectQuery({
    variables: {
      xTenantId,
      externalResourceId: userProfileId,
      externalResource: 'UserProfileId',
    },
  });

  const addressVerificationRef = (node: HTMLDivElement | null): void => {
    setAddressVerificationElement(node);
  };

  const fetchIframeUrl = async (): Promise<void> => {
    setVerificationState({ step: 'id', status: 'loading', view: 'open' });
    try {
      const url = await fetchUploaderIframeURL(userProfileId);

      setIframeUrl(url + '&embedded=true');
      setVerificationState({ step: 'id', status: 'idle', view: 'open' });
    } catch (error) {
      setVerificationState({ step: 'id', status: 'failed', view: 'open' });
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      const allowedOrigin =
        process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION' ? 'agechecked.getid.ee' : 'agechecked.sb.getid.dev';

      if (!event.origin.includes(allowedOrigin) || !('getidEventName' in event.data)) {
        return;
      }

      const { getidEventName } = event.data;

      if (getidEventName === 'complete') {
        const addressNeeded =
          compliance?.addressVerificationStatus !== 'Verified' &&
          (compliance?.kycLevel === 0 || (compliance?.kycLevel === 1 && hasRequiredTag(data?.tagsObject)));

        setVerificationState(
          addressNeeded
            ? { step: 'address', status: 'idle', view: 'open' }
            : { step: 'complete', status: 'success', view: 'open' }
        );
      }

      if (getidEventName === 'fail') {
        setVerificationState({ step: 'id', status: 'failed', view: 'open' });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [compliance, data?.tagsObject]);

  const hasRequiredTag = (tags: TagsObjectQuery['tagsObject']): boolean => {
    if (!tags) return false;

    return tags.some((tagObj) => tagObj.tag?.name === '2KDOCSREQUIRED' || tagObj.tag?.name === '2KNotice');
  };

  const loadAC9124SDK = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.AC9124) {
        resolve();

        return;
      }

      const script = document.createElement('script');

      script.id = 'ac9124-script';
      script.src = process.env.REACT_APP_AC_SDK_URL as string;
      script.onload = (): void => resolve();
      script.onerror = (): void => reject(new Error('Failed to load AC9124 SDK'));
      document.head.appendChild(script);
    });
  };

  useEffect(() => {
    if (
      verificationState.step === 'address' &&
      verificationState.view === 'open' &&
      !addressVerificationInitialized &&
      addressVerificationElement
    ) {
      const initializeAddressVerification = async (): Promise<void> => {
        try {
          await loadAC9124SDK();

          if (window.AC9124 && addressVerificationElement) {
            const config: AddressVerificationConfig = {
              merchantkey: process.env.REACT_APP_AC_MERCHANT_KEY as string,
              name: user?.givenName || '',
              surname: user?.familyName || '',
              street: user?.address?.streetAddress || '',
              city: user?.address?.addressLocality || '',
              postcode: user?.address?.postalCode || '',
              country: user?.address?.addressCountry || '',
              countrycode: 'GB',
              locale: 'en-GB',
              reference: userProfileId,
              onInit: (data) => {
                setAddressVerificationInitialized(true);
                setIsAddressVerificationLoading(false);
              },
              onComplete: (data) => {
                setVerificationState({ step: 'complete', status: 'success', view: 'open' });
              },
              onFail: (data) => {
                setVerificationState({ step: 'address', status: 'failed', view: 'open' });
              },
            };

            const ac9124 = new window.AC9124();

            ac9124.init(config);
          }
        } catch (error) {
          setVerificationState({ step: 'address', status: 'failed', view: 'open' });
        }
      };

      initializeAddressVerification();
    }
  }, [
    verificationState.step,
    verificationState.view,
    addressVerificationInitialized,
    user,
    userProfileId,
    addressVerificationElement,
  ]);

  useEffect(() => {
    if (!compliance || !userProfileId) return;

    const hasCompletedUpload = localStorage.getItem(UPLOAD_COMPLETED_KEY) === 'true';

    const idNeeded =
      compliance?.idVerificationStatus !== 'Verified' &&
      (compliance?.kycLevel === 0 || (compliance?.kycLevel === 1 && hasRequiredTag(data?.tagsObject)));
    const addressNeeded =
      compliance?.addressVerificationStatus !== 'Verified' &&
      (compliance?.kycLevel === 0 || (compliance?.kycLevel === 1 && hasRequiredTag(data?.tagsObject)));

    const shouldDisplay = isDocUploaderEnabled && (idNeeded || addressNeeded) && !hasCompletedUpload;

    // Reset states if we shouldn't display
    if (!shouldDisplay) {
      if (verificationState.view !== 'closed') {
        setVerificationState({ step: 'complete', status: 'success', view: 'closed' });
      }

      return;
    }

    if (verificationState.view === 'closed') {
      const nextStep = idNeeded ? 'id' : 'address';

      setVerificationState({ step: nextStep, status: 'idle', view: 'prompt' });
    }
  }, [userProfileId, compliance, data?.tagsObject, isDocUploaderEnabled, verificationState.view]);

  useEffect(() => {
    if (verificationState.step === 'id' && verificationState.view === 'open' && !iframeUrl) {
      fetchIframeUrl();
    }
  }, [verificationState.step, verificationState.view, iframeUrl]);

  const handlePromptClose = (): void => {
    if (verificationState.step === 'id') {
      setVerificationState({ step: 'id', status: 'idle', view: 'open' });
    } else if (verificationState.step === 'address') {
      setVerificationState({ step: 'address', status: 'idle', view: 'open' });
    }
  };

  const handleClose = (): void => {
    setVerificationState({ step: 'id', status: 'idle', view: 'closed' });
    localStorage.setItem(UPLOAD_COMPLETED_KEY, 'true');
  };

  const handleRetry = (): void => {
    if (verificationState.step === 'address') {
      setAddressVerificationInitialized(false);
      setIsAddressVerificationLoading(true);
      setVerificationState({ step: 'address', status: 'idle', view: 'open' });
    } else {
      fetchIframeUrl();
    }
  };

  // If compliance check fails or user has already completed upload in this session, don't render anything
  if (
    !isDocUploaderEnabled ||
    !compliance ||
    !(compliance.kycLevel === 0 || (compliance.kycLevel === 1 && hasRequiredTag(data?.tagsObject))) ||
    localStorage.getItem(UPLOAD_COMPLETED_KEY) === 'true'
  ) {
    return null;
  }

  const renderContent = (): ReactNode => {
    if (verificationState.step === 'complete') {
      return (
        <div className="doc-uploader__message doc-uploader__complete">
          <Paragraph>{t('document-uploader-complete-message')}</Paragraph>
        </div>
      );
    }

    if (verificationState.status === 'loading') {
      return (
        <div className="spinner-wrapper">
          <LogoSpinner />
        </div>
      );
    }

    if (verificationState.status === 'failed') {
      return (
        <div className="doc-uploader__message doc-uploader__complete">
          <Paragraph>
            {t('document-uploader-failed-message')}{' '}
            <Button type="button" variant="text" onClick={handleRetry}>
              {t('document-uploader-try-again')}
            </Button>
          </Paragraph>
        </div>
      );
    }

    if (verificationState.step === 'id') {
      return iframeUrl ? (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title="ID Document Uploader"
          allow="camera; microphone"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-top-navigation"
        />
      ) : (
        <div className="spinner-wrapper">
          <LogoSpinner />
        </div>
      );
    }

    return (
      <div className="address-verification-container">
        {isAddressVerificationLoading && (
          <div className="spinner-wrapper spinner-wrapper--overlay">
            <LogoSpinner />
          </div>
        )}
        <div id="ac-component" ref={addressVerificationRef} />
      </div>
    );
  };

  const getModalTitle = (): string => {
    return verificationState.step === 'id'
      ? 'ID Document Upload'
      : verificationState.step === 'address'
        ? 'Proof of Address Verification'
        : 'Document Verification';
  };

  return (
    <>
      <div className="doc-uploader">
        <Prompt
          PrimaryButton={{ text: 'Start Verification', onClick: handlePromptClose }}
          show={verificationState.view === 'prompt'}
          title="Document Verification Required"
          onClose={handlePromptClose}
        >
          {t('documentUpload.prompt')}
        </Prompt>

        <PopUpHandler
          elementToFocusOnOpen={iframeRef}
          isOpen={verificationState.view === 'open'}
          header={getModalTitle()}
          onClose={handleClose}
          modalProps={{ className: 'doc-uploader__modal' }}
          drawerProps={{ className: 'doc-uploader__modal' }}
        >
          {renderContent()}
        </PopUpHandler>
      </div>
    </>
  );
};
